import { memo } from "react";

import type { BillingTypeTarget, InputMaybe, OptionType } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types";

import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

import { QuestionSectionWrapper } from "../../../../../_shared/ui/components/system/question/_layouts/item-wrapper";

type Props = {
	isEdited: boolean;
	hasError: boolean;

	optionType: ForNodeType<OptionType>;
	onSetOptionType: (input: string) => void;
	optionTypeOptions: FormOptionType[];

	shouldShowBillingTypeTarget: boolean;
	billingTypeTarget: ForNodeType<BillingTypeTarget>;
	onSetBillingTypeTarget: (input: string) => void;
	billingTypeTargetOptions: FormOptionType[];

	maxBuyableNumPerTarget: ForNodeType<InputMaybe<number> | undefined> | undefined;
	onSetMaxBuyableNumPerTarget: (input: number | undefined) => void;

	title?: string;
	guide?: string;
};

export const QuestionOptionSalesType = memo(
	({
		isEdited,
		hasError,

		optionType,
		onSetOptionType,
		optionTypeOptions,

		shouldShowBillingTypeTarget,
		billingTypeTarget,
		onSetBillingTypeTarget,
		billingTypeTargetOptions,

		maxBuyableNumPerTarget,
		onSetMaxBuyableNumPerTarget,

		title: _title,
		guide: _guide,
	}: Props) => {
		const title = _title ?? "購入可能な数・販売できる単位";
		const guide =
			_guide ??
			"ゲストが一度の予約でいくつ購入することができるかを設定しましょう。<br />日ごとに選択可能なサービスの場合は、日ごとに購入可能な上限になります。<br />「お子様１人あたり」に設定すると、お子様のみが選択できるオプションを作成できます。";

		return (
			<FeatureIslandCategoryQuestionForMain
				required={true}
				title={title}
				guide={guide}
				isEdited={isEdited}
				hasError={hasError}
				inputChildren={
					<QuestionSectionWrapper>
						<div className="flex flex-col gap-y-4">
							<div className="flex items-center gap-x-2">
								<p>オプションのタイプ：</p>
								<SelectField
									value={optionType.value}
									options={optionTypeOptions}
									errorMessage={optionType.error}
									handleValueChange={onSetOptionType}
								/>
							</div>

							<div className="flex gap-x-2">
								{shouldShowBillingTypeTarget && (
									<SelectField
										value={billingTypeTarget.value}
										options={billingTypeTargetOptions}
										errorMessage={billingTypeTarget.error}
										handleValueChange={onSetBillingTypeTarget}
									/>
								)}

								<div className="flex items-end gap-x-2">
									<InputNumberField
										value={maxBuyableNumPerTarget?.value ?? undefined}
										handleInput={onSetMaxBuyableNumPerTarget}
										errorMessage={maxBuyableNumPerTarget?.error}
										hiddenSpinButton={false}
									/>
									<p>個まで</p>
								</div>
							</div>
						</div>
					</QuestionSectionWrapper>
				}
			/>
		);
	},
);

QuestionOptionSalesType.displayName = "QuestionOptionSalesType";
